import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Truck,
  Radio,
  MapPin,
  ChevronRight,
  Zap,
  Settings,
  Package,
} from 'lucide-react';
import { deliveryService } from '@services/delivery.service';
import { PageGuard } from '@components/PageGuard';
import { cn } from '@lib/utils';

// ── Provider cards config ─────────────────────────────────────────────────────

interface ProviderCard {
  slug: string;
  name: string;
  description: string;
  color: string;
  initial: string;
  path: string;
}

const PROVIDERS: ProviderCard[] = [
  {
    slug: 'bolt',
    name: 'Bolt Food',
    description: 'Bolt third-party delivery integration',
    color: 'bg-[var(--chart-1)]',
    initial: 'B',
    path: '/delivery/providers/bolt',
  },
  {
    slug: 'glovo',
    name: 'Glovo',
    description: 'Glovo courier network integration',
    color: 'bg-[var(--chart-3)]',
    initial: 'G',
    path: '/delivery/providers/glovo',
  },
  {
    slug: 'uber',
    name: 'Uber Eats',
    description: 'Uber Eats delivery integration',
    color: 'bg-[var(--chart-2)]',
    initial: 'U',
    path: '/delivery/providers/uber',
  },
  {
    slug: 'indriver',
    name: 'InDriver',
    description: 'InDriver on-demand delivery integration',
    color: 'bg-[var(--chart-4)]',
    initial: 'I',
    path: '/delivery/providers/indriver',
  },
  {
    slug: 'demo',
    name: 'Demo Provider',
    description: 'Sandbox testing provider, always available',
    color: 'bg-[var(--chart-6)]',
    initial: 'D',
    path: '/delivery/providers/demo',
  },
];

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  loading?: boolean;
}

function StatCard({ label, value, sub, icon: Icon, loading }: StatCardProps) {
  return (
    <div className="rounded-3xl bg-white shadow-soft p-6 flex items-start gap-4">
      <div className="w-11 h-11 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">{label}</p>
        {loading ? (
          <div className="mt-1 h-7 w-16 rounded-xl bg-[var(--bg-elevated)] animate-pulse" />
        ) : (
          <p className="text-[28px] font-semibold tracking-[-0.02em] text-[var(--text-primary)] leading-tight">
            {value}
          </p>
        )}
        {sub && !loading && (
          <p className="text-[12px] text-[var(--text-muted)] mt-0.5">{sub}</p>
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const DeliveryOverviewPage = () => {
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['delivery', 'providers', 'summary'],
    queryFn: () => deliveryService.getProviderSummary(),
  });

  const { data: broadcastStats, isLoading: broadcastLoading } = useQuery({
    queryKey: ['delivery', 'broadcasts', 'stats'],
    queryFn: () => deliveryService.getBroadcastStats(),
    refetchInterval: 30000,
  });

  const { data: tripStats, isLoading: tripLoading } = useQuery({
    queryKey: ['delivery', 'trips', 'stats'],
    queryFn: () => deliveryService.getTripListingStats(),
    refetchInterval: 30000,
  });

  const totalProviders =
    summary
      ? summary.bolt + summary.glovo + summary.uber + summary.indriver
      : 0;

  return (
    <PageGuard permission="settings.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
        {/* Page header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Platform
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Delivery
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            Third-party provider integrations, fleet settings, and dispatch activity
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Active integrations"
            value={totalProviders}
            sub="across all merchants"
            icon={Truck}
            loading={summaryLoading}
          />
          <StatCard
            label="Broadcasts today"
            value={broadcastStats?.totalToday ?? 0}
            sub={`${broadcastStats?.openNow ?? 0} open right now`}
            icon={Radio}
            loading={broadcastLoading}
          />
          <StatCard
            label="Trip listings open"
            value={tripStats?.openListings ?? 0}
            sub={`${tripStats?.claimedToday ?? 0} claimed today`}
            icon={MapPin}
            loading={tripLoading}
          />
          <StatCard
            label="Broadcasts expired"
            value={broadcastStats?.expiredToday ?? 0}
            sub="today: no rider found"
            icon={Package}
            loading={broadcastLoading}
          />
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to="/delivery/providers"
            className="rounded-3xl bg-white shadow-soft p-6 flex items-center gap-4 hover:shadow-md transition-all group"
          >
            <div className="w-11 h-11 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center">
              <Truck className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-semibold text-[var(--text-primary)]">Manage Providers</p>
              <p className="text-[13px] text-[var(--text-muted)]">
                View and configure third-party delivery provider integrations
              </p>
            </div>
            <ChevronRight
              className="w-5 h-5 text-[var(--text-muted)] group-hover:text-[var(--text-primary)] transition-colors"
              strokeWidth={1.5}
            />
          </Link>

          <Link
            to="/delivery/settings"
            className="rounded-3xl bg-white shadow-soft p-6 flex items-center gap-4 hover:shadow-md transition-all group"
          >
            <div className="w-11 h-11 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center">
              <Settings className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-semibold text-[var(--text-primary)]">Delivery Settings</p>
              <p className="text-[13px] text-[var(--text-muted)]">
                Surcharge rates, vehicle rules, and Chekit fleet configuration
              </p>
            </div>
            <ChevronRight
              className="w-5 h-5 text-[var(--text-muted)] group-hover:text-[var(--text-primary)] transition-colors"
              strokeWidth={1.5}
            />
          </Link>
        </div>

        {/* Provider cards */}
        <div>
          <h2 className="text-[18px] font-semibold text-[var(--text-primary)] mb-4">Supported Providers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROVIDERS.map((p) => {
              const configCount =
                !summaryLoading && summary
                  ? (summary[p.slug as keyof typeof summary] ?? 0)
                  : null;

              return (
                <div
                  key={p.slug}
                  className="rounded-3xl bg-white shadow-soft p-6 flex flex-col gap-4"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-[18px]',
                        p.color,
                      )}
                    >
                      {p.initial}
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-[var(--text-primary)]">{p.name}</p>
                      <p className="text-[12px] text-[var(--text-muted)]">{p.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-[var(--text-muted)]">
                      {configCount !== null
                        ? `Configured by ${configCount} merchant${configCount !== 1 ? 's' : ''}`
                        : 'Loading...'}
                    </span>
                    {p.slug === 'demo' && (
                      <span className="rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[11px] font-semibold px-2.5 py-1">
                        Sandbox
                      </span>
                    )}
                  </div>

                  <Link
                    to={p.path}
                    className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200 self-start"
                  >
                    <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Configure
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PageGuard>
  );
};
